import { useNavigate } from 'react-router-dom';
import { logEvent } from '@amplitude/analytics-browser';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import Typography from '../Typography';
import Button from '../Button';
import { useLazyTranslation } from '../../hooks/i18n';
import { BANKING_RFIS } from '../../constants/auth';
import { bankingEvents } from '../../constants/events';
import { PendingRfi } from '../../types/banking';
import { COLORS } from '../../themev2';

interface RFIActionRequiredBannerProps {
  rfis?: PendingRfi[];
}

// Shown whenever there is any pending (action-required) RFI of any type — i.e.
// any RFI that is not yet answered/closed. The CTA takes the user to the RFIs
// section, where they pick the specific one to resolve.
const RFIActionRequiredBanner = ({ rfis = [] }: RFIActionRequiredBannerProps) => {
  const navigate = useNavigate();
  const { isLoading, t } = useLazyTranslation(['banking-page']);

  if (isLoading || rfis.length === 0) return null;

  const handleResolve = () => {
    logEvent(bankingEvents.RFI_BANNER_CTA_CLICKED, { 'RFI Count': rfis.length });
    navigate(BANKING_RFIS);
  };

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          backgroundColor: COLORS.orange[100],
          border: `1px solid ${COLORS.orange[700]}`,
          borderRadius: '8px',
          padding: '16px 20px',
        }}
      >
        <WarningAmberRoundedIcon sx={{ color: COLORS.orange[800] }} />
        <Box sx={{ flex: 1, minWidth: '220px' }}>
          <Typography fontWeight={700} sx={{ color: COLORS.gray[1300] }}>
            {t('banking-page:rfi.banner.title')}
          </Typography>
          <Typography sx={{ color: COLORS.gray[1300] }}>
            {t('banking-page:rfi.banner.description')}
          </Typography>
        </Box>
        <Button color="secondary" onClick={handleResolve}>
          {t('banking-page:rfi.banner.cta')}
        </Button>
      </Box>
    </Grid>
  );
};

export default RFIActionRequiredBanner;
